import { useEffect, useState } from 'react';

import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';

/**
 * O alvo do mês, no vocabulário certo pra cada papel.
 *
 * ── META E OBJETIVO NÃO SÃO SINÔNIMOS AQUI ──────────────────────────────────
 * Especialista não é contratado e assume um OBJETIVO (Desafio Pessoal, view
 * desafio_pessoal_progresso). Gerente é contratado e responde por uma META
 * que não escolheu (meta_north, definida pelo super admin). A regra completa
 * está documentada em campanhas/useMetaNorth.ts — aqui só se aplica.
 *
 * O realizado do gestor vem POR FORA (prop): é a produção do escopo que o
 * Início já carregou — calcular de novo aqui seria a segunda fonte do mesmo
 * número na mesma tela.
 */

export type ObjetivoDoMes = {
  /** 'objetivo' (especialista) ou 'meta' (gestão). Decide os textos. */
  vocabulario: 'objetivo' | 'meta';
  alvo: number;
  realizado: number;
  pct: number;
  falta: number;
  diasRestantes: number;
  /** Projeção no ritmo atual, em % do alvo. Null no dia 1 (sem base). */
  ritmoPct: number | null;
};

type Entrada = {
  scopeLoading: boolean;
  isSuperAdmin: boolean;
  isManager: boolean;
  currentCrmUserId: number | null;
  viewAsCrmUserId: number | null;
  /** Produção do mês do escopo visível (vendasMesValor). */
  realizadoEscopo: number;
};

const agora = () => {
  const now = new Date();
  const ano = now.getFullYear();
  const mes = now.getMonth() + 1;
  const diaHoje = now.getDate();
  const diasNoMes = new Date(ano, mes, 0).getDate();
  return { ano, mes, diaHoje, diasNoMes };
};

const ritmo = (realizado: number, alvo: number, diaHoje: number, diasNoMes: number) =>
  diaHoje >= 2 && alvo > 0
    ? Math.round(((realizado / diaHoje) * diasNoMes * 100) / alvo)
    : null;

export const useObjetivoDoMes = ({
  scopeLoading,
  isSuperAdmin,
  isManager,
  currentCrmUserId,
  viewAsCrmUserId,
  realizadoEscopo,
}: Entrada) => {
  const [objetivo, setObjetivo] = useState<ObjetivoDoMes | null>(null);

  useEffect(() => {
    if (scopeLoading) return;
    let ativo = true;
    const { ano, mes, diaHoje, diasNoMes } = agora();
    const diasRestantes = diasNoMes - diaHoje;

    (async () => {
      // Especialista — e o super admin "vendo como" um: o Desafio Pessoal da
      // pessoa observada, que é o que ela mesma veria.
      const pessoal = viewAsCrmUserId ?? (!isSuperAdmin && !isManager ? currentCrmUserId : null);
      if (pessoal != null) {
        const { data } = await supabase
          .from('desafio_pessoal_progresso')
          .select('meta, realizado, pct, falta, dias_restantes')
          .eq('crm_user_id', pessoal)
          .maybeSingle();
        if (!ativo) return;
        // Sem objetivo definido o card some. "Objetivo zero" seria mentira:
        // quem não definiu não é quem definiu zero.
        if (!data || data.meta == null || Number(data.meta) <= 0) {
          setObjetivo(null);
          return;
        }
        const alvo = Number(data.meta);
        const realizado = Number(data.realizado ?? 0);
        setObjetivo({
          vocabulario: 'objetivo',
          alvo,
          realizado,
          pct: Number(data.pct ?? 0),
          falta: Number(data.falta ?? Math.max(0, alvo - realizado)),
          diasRestantes: Number(data.dias_restantes ?? diasRestantes),
          ritmoPct: ritmo(realizado, alvo, diaHoje, diasNoMes),
        });
        return;
      }

      // Gestão: a soma das metas North visíveis (RLS: gerente vê a própria,
      // super admin vê todas) contra a produção do escopo.
      const { data } = await supabase
        .from('meta_north')
        .select('valor')
        .eq('ano', ano)
        .eq('mes', mes);
      if (!ativo) return;
      const alvo = ((data ?? []) as { valor: number | string }[]).reduce(
        (s, r) => s + Number(r.valor || 0),
        0,
      );
      if (alvo <= 0) {
        setObjetivo(null);
        return;
      }
      setObjetivo({
        vocabulario: 'meta',
        alvo,
        realizado: realizadoEscopo,
        pct: Math.min(100, Math.round((realizadoEscopo * 100) / alvo)),
        falta: Math.max(0, alvo - realizadoEscopo),
        diasRestantes,
        ritmoPct: ritmo(realizadoEscopo, alvo, diaHoje, diasNoMes),
      });
    })();

    return () => {
      ativo = false;
    };
  }, [scopeLoading, isSuperAdmin, isManager, currentCrmUserId, viewAsCrmUserId, realizadoEscopo]);

  return objetivo;
};
